import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import AccountService, { Account } from '../../services/account.service';

const AccountForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedParent, setSelectedParent] = useState<any>(null);

  const [formData, setFormData] = useState<Account>({
    accountNumber: '',
    accountName: '',
    accountType: 'asset',
    balance: 0,
    currency: 'OMR',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    fetchAccounts();
    if (isEdit && id) {
      fetchAccount(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await AccountService.getAll({
        'pagination[pageSize]': 100,
        sort: 'accountNumber:asc',
      });
      setAccounts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchAccount = async (accountId: number) => {
    try {
      setLoading(true);
      const response = await AccountService.getOne(accountId);
      const accountData = response.data?.data;

      if (accountData?.attributes) {
        const attrs = accountData.attributes;
        setFormData({
          accountNumber: attrs.accountNumber,
          accountName: attrs.accountName,
          accountType: attrs.accountType,
          parentAccount: attrs.parentAccount?.data?.id,
          balance: attrs.balance || 0,
          currency: attrs.currency || 'OMR',
          description: attrs.description || '',
          isActive: attrs.isActive !== false,
        });

        if (attrs.parentAccount?.data) {
          setSelectedParent(attrs.parentAccount.data);
        }
      }
    } catch (error) {
      console.error('Error fetching account:', error);
      setError('Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const validateForm = (): boolean => {
    if (!formData.accountNumber) {
      setError('Account number is required');
      return false;
    }
    if (!formData.accountName) {
      setError('Account name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (isEdit && id) {
        await AccountService.update(parseInt(id), formData);
        setSuccess('Account updated!');
      } else {
        await AccountService.create(formData);
        setSuccess('Account created!');
      }

      setTimeout(() => {
        navigate('/accounts');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving account:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save account. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Account' : 'New Account'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Account Number"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                  helperText="e.g., 1000, 2000, 4000"
                />
                <TextField
                  fullWidth
                  required
                  label="Account Name"
                  name="accountName"
                  value={formData.accountName}
                  onChange={handleChange}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Account Type"
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleChange}
                >
                  <MenuItem value="asset">Asset</MenuItem>
                  <MenuItem value="liability">Liability</MenuItem>
                  <MenuItem value="equity">Equity</MenuItem>
                  <MenuItem value="revenue">Revenue</MenuItem>
                  <MenuItem value="expense">Expense</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                />
              </Stack>

              <Autocomplete
                value={selectedParent}
                onChange={(e, v) => {
                  setSelectedParent(v);
                  setFormData({ ...formData, parentAccount: v?.id });
                }}
                options={accounts.filter((a) => a.id !== parseInt(id || '0'))}
                loading={accountsLoading}
                getOptionLabel={(o) => `${o.attributes?.accountNumber} - ${o.attributes?.accountName}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Account (Optional)"
                    helperText="For sub-accounts under a main account"
                  />
                )}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={formData.isActive !== false}
                    onChange={handleChange}
                  />
                }
                label="Active Account"
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/accounts')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Account' : 'Create Account'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default AccountForm;

